<template>
  <v-combobox
    v-model="model"
    :items="suggestionItems"
    :search-input.sync="search"
    :rules="rulesList"
    :label="label"
    :disabled="disabled"
    :dense="dense"
    :hide-details="hideDetails"
    :no-filter="true"
    multiple
    clearable
    hide-selected
    small-chips
    flat
    outlined
    @update:search-input="onSearchInput"
    @focus="onFocus"
    @blur="onBlur"
  >
    <template v-slot:append>
      <slot name="appendActions" />
      <v-menu
        v-if="canReadPerson"
        v-model="isPersonSearchOpen"
        :close-on-content-click="false"
        offset-y
        min-width="350"
        max-width="450"
      >
        <template #activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            icon
            x-small
            class="mt-1"
            tabindex="-1"
            :aria-label="$t('components.template.dialog.preview.searchReceiver')"
            :title="$t('components.template.dialog.preview.searchReceiver')"
          >
            <v-icon small>mdi-account-search</v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-text class="pb-0">
            <v-autocomplete
              v-model="selectedPerson"
              :items="filteredPersons"
              :loading="isSearchingPersons"
              :search-input.sync="personSearchPattern"
              item-text="titleReadable"
              item-value="id"
              :label="$t('components.template.dialog.preview.searchReceiver')"
              :placeholder="$t('components.partner.PartnerReportCreateForm.driverPlaceholder')"
              return-object
              clearable
              outlined
              dense
              autofocus
              :no-filter="true"
              @update:search-input="debouncePersonSearch"
            >
              <template #no-data>
                <v-list-item v-if="isSearchingPersons">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ $t("components.partner.PartnerReportCreateForm.searchingPersons") }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item v-else>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{
                        personSearchPattern && personSearchPattern.length >= 2
                          ? $t("components.partner.PartnerReportCreateForm.noPersonsFound")
                          : $t("components.partner.PartnerReportCreateForm.searchForPerson")
                      }}
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template #item="{ item }">
                <refs-person :item="item">
                  <template #icon>
                    <v-list-item-avatar>
                      <v-icon>mdi-account</v-icon>
                    </v-list-item-avatar>
                  </template>
                </refs-person>
              </template>
              <template #selection="{ item }">
                <v-icon small color="success" class="mr-2">mdi-check-circle</v-icon>
                <span class="pr-2">{{ item.firstName }} {{ item.lastName }}</span>
              </template>
            </v-autocomplete>

            <v-select
              v-if="selectedPerson && selectedPerson.emails && selectedPerson.emails.length > 1"
              v-model="selectedPersonEmail"
              :items="selectedPerson.emails"
              item-text="email"
              item-value="email"
              :label="$t('common.nouns.email')"
              outlined
              dense
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text small @click="isPersonSearchOpen = false">
              {{ $t("close") }}
            </v-btn>
            <v-btn color="info" small elevation="0" :disabled="!resolvedPersonEmail" @click="applyPerson">
              {{ $t("confirm") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </template>

    <template v-slot:item="{ item }">
      <v-list-item-avatar>
        <v-avatar color="primary" class="white--text">
          {{ avatar(item) }}
        </v-avatar>
      </v-list-item-avatar>
      <v-list-item-content>
        <v-list-item-title>{{ personLabelFor(item) || item }}</v-list-item-title>
        <v-list-item-subtitle v-if="personLabelFor(item)">{{ item }}</v-list-item-subtitle>
      </v-list-item-content>
    </template>

    <template v-slot:selection="{ attrs, item, parent, selected }">
      <v-chip
        v-bind="attrs"
        :input-value="selected"
        :color="disabled ? '#F3F3F3' : 'light-grey'"
        :x-small="dense"
        label
        small
        outlined
      >
        <span class="pr-2">{{ item }}</span>
        <v-icon small @click="removeItem(item, parent)" :color="disabled ? 'grey' : 'light-grey'">$delete</v-icon>
      </v-chip>
    </template>
  </v-combobox>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { debounce } from "debounce";
import { requiredRule } from "@/lib/rules/requiredRule";
import { PersonSearchModule } from "@/store/modules/search.store";
import { UserModule } from "@/store/modules/me-user.store";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { ResourceEnum } from "@/store/enum/authResourceEnum";
import { IPerson } from "@/models/person.entity";
import { MrfiktivPersonControllerGetParamsGen } from "@/services/mrfiktiv/v1/data-contracts";
import { avatar } from "@/lib/utility/mail-helper";
import RefsPerson from "@/components/utility/RefsPerson.vue";

@Component({ components: { RefsPerson } })
export default class RecipientSelect extends Vue {
  @Prop({ default: () => [] })
  items!: string[];

  @Prop()
  prefill?: string[];

  @Prop()
  rules?: Function[];

  @Prop({ default: () => [] })
  recommendedReceivers!: string[];

  @Prop()
  partnerId?: string;

  @Prop()
  label?: string;

  @Prop({ default: false })
  disabled?: boolean;

  @Prop()
  dense?: boolean;

  @Prop()
  hideDetails?: boolean | string;

  @Prop({ default: true })
  isRequired?: boolean;

  model: string[] = this.prefill !== undefined ? [...this.prefill] : [...(this.items ?? [])];

  search: string | null = null;
  isFocused = false;

  /** Backend email suggestions for the current query (local copy, not read reactively from the store). */
  emailSuggestions: string[] = [];
  /** email -> "First Last" label, for rendering suggestions as name + email. */
  personLabelByEmail: Record<string, string> = {};

  // Person-search menu state
  isPersonSearchOpen = false;
  personSearchPattern = "";
  isSearchingPersons = false;
  selectedPerson: IPerson | null = null;
  selectedPersonEmail: string | null = null;

  debouncePersonSearch = debounce((val: string) => {
    this.personSearchPattern = val;
    this.searchPersons();
  }, 300);

  debounceEmailSearch = debounce((val: string) => {
    this.searchEmails(val);
  }, 300);

  get rulesList() {
    return [this.forEachRules];
  }

  get filteredPersons(): IPerson[] {
    return (PersonSearchModule.filtered as IPerson[]) || [];
  }

  get canReadPerson(): boolean {
    return UserModule.abilities.can(ActionEnum.READ, ResourceEnum.PERSON, this.partnerId);
  }

  /** When the field is empty/focused show recommendations; while typing show backend suggestions. */
  get suggestionItems(): string[] {
    if (this.search && this.search.length >= 2) {
      return this.emailSuggestions;
    }
    if (this.isFocused) {
      return (this.recommendedReceivers ?? []).filter((email) => !this.model.includes(email));
    }
    return [];
  }

  /** The email to apply from the person menu: the explicit pick, or the person's first email. */
  get resolvedPersonEmail(): string | null {
    if (this.selectedPersonEmail) {
      return this.selectedPersonEmail;
    }
    return this.selectedPerson?.emails?.[0]?.email ?? null;
  }

  mounted() {
    this.emitSelection();
  }

  personLabelFor(email: string): string {
    return this.personLabelByEmail[email] ?? "";
  }

  /** Initials avatar for a receiver email, matching the message-list display. */
  avatar(email: string): string {
    return avatar(email);
  }

  onFocus() {
    this.isFocused = true;
  }

  onSearchInput(val: string | null) {
    if (!val || val.length < 2) {
      this.emailSuggestions = [];
      return;
    }
    this.debounceEmailSearch(val);
  }

  async searchEmails(pattern: string) {
    const partnerId = this.partnerId;
    if (!pattern || pattern.length < 2 || !partnerId) {
      this.emailSuggestions = [];
      return;
    }
    try {
      PersonSearchModule.setSearch(pattern);
      const query: MrfiktivPersonControllerGetParamsGen = { partnerId };
      const persons = (await PersonSearchModule.fetchPage(query)) ?? [];
      const labelByEmail: Record<string, string> = {};
      const emails: string[] = [];
      for (const person of persons) {
        const name = `${person.firstName ?? ""} ${person.lastName ?? ""}`.trim();
        for (const e of person.emails ?? []) {
          if (!e.email || emails.includes(e.email)) continue;
          emails.push(e.email);
          if (name) labelByEmail[e.email] = name;
        }
      }
      this.personLabelByEmail = labelByEmail;
      this.emailSuggestions = emails;
    } catch (error) {
      this.$log.error(error);
    }
  }

  async searchPersons() {
    const partnerId = this.partnerId;
    if (!this.personSearchPattern || this.personSearchPattern.length < 2 || !partnerId) return;
    this.isSearchingPersons = true;
    try {
      PersonSearchModule.setSearch(this.personSearchPattern);
      const query: MrfiktivPersonControllerGetParamsGen = { partnerId };
      await PersonSearchModule.fetchPage(query);
    } catch (error) {
      this.$log.error(error);
    } finally {
      this.isSearchingPersons = false;
    }
  }

  @Watch("selectedPerson")
  onPersonSelected() {
    this.selectedPersonEmail = this.selectedPerson?.emails?.[0]?.email ?? null;
  }

  applyPerson() {
    const email = this.resolvedPersonEmail;
    if (!email) return;
    if (!this.model.includes(email)) {
      this.model = [...this.model, email];
    }
    this.selectedPerson = null;
    this.selectedPersonEmail = null;
    this.personSearchPattern = "";
    this.isPersonSearchOpen = false;
  }

  removeItem(item: string, parent: any) {
    parent.selectItem(item);
  }

  forEachRules(values: string[]) {
    const resultMessages: string[] = [];
    for (const rule of this.rules || []) {
      let message = "";
      (values || []).forEach((value) => {
        const res = rule(value);
        if (res !== true) {
          message = res as string;
        }
      });
      if (message) resultMessages.push(message);
    }
    if (this.isRequired && this.model?.length === 0) {
      resultMessages.push(requiredRule()("") as string);
    }
    return resultMessages.length > 0 ? resultMessages.join(", ") : true;
  }

  get isValid(): boolean {
    return this.forEachRules(this.model) === true;
  }

  @Watch("model")
  emitSelection() {
    this.$emit("changeSelection", this.model);
    this.$emit("changeIsValid", this.isValid);
  }

  onBlur() {
    this.isFocused = false;
    this.$emit("blur");
  }
}
</script>
